import type { Fetcher } from 'swr'
import { del, get, patch, post, put } from './base'
import type {
  CommonResponse,
  FileUploadConfigResponse,
  ICurrentWorkspace,
  IWorkspace,
  InitValidateStatusResponse,
  InvitationResponse,
  LangGeniusVersionResponse,
  Member,
  OauthResponse,
  SetupStatusResponse,
  UserProfileOriginResponse,
} from '@/models/common'
import type { SystemFeatures } from '@/types/feature'

export const login: Fetcher<
  CommonResponse & { data: string },
  { url: string; body: Record<string, any> }
> = ({ url, body }) => {
  return post(url, { body }) as Promise<CommonResponse & { data: string }>
}

export const signup: Fetcher<
  CommonResponse,
  { url: string; body: Record<string, any> }
> = ({ url, body }) => {
  return post(url, { body })
}

export const setup: Fetcher<CommonResponse, { body: Record<string, any> }> = ({
  body,
}) => {
  return post<CommonResponse>('/setup', { body })
}

export const initValidate: Fetcher<
  CommonResponse,
  { body: Record<string, any> }
> = ({ body }) => {
  return post<CommonResponse>('/init', { body })
}

export const fetchInitValidateStatus = () => {
  return get<InitValidateStatusResponse>('/init')
}

export const fetchSetupStatus = () => {
  return get<SetupStatusResponse>('/setup')
}

export const fetchUserProfile: Fetcher<
  UserProfileOriginResponse,
  { url: string; params: Record<string, any> }
> = ({ url, params }) => {
  return get<UserProfileOriginResponse>(url, params, {
    needAllResponseContent: true,
  })
}

export const updateUserProfile: Fetcher<
  CommonResponse,
  { url: string; body: Record<string, any> }
> = ({ url, body }) => {
  return post<CommonResponse>(url, { body })
}

export const logout: Fetcher<
  CommonResponse,
  { url: string; params: Record<string, any> }
> = ({ url, params }) => {
  return get<CommonResponse>(url, params)
}

export const fetchLanggeniusVersion: Fetcher<
  LangGeniusVersionResponse,
  { url: string; params: Record<string, any> }
> = ({ url, params }) => {
  return get<LangGeniusVersionResponse>(url, { params })
}

export const oauth: Fetcher<
  OauthResponse,
  { url: string; params: Record<string, any> }
> = ({ url, params }) => {
  return get<OauthResponse>(url, { params })
}

export const oneMoreStep: Fetcher<
  CommonResponse,
  { url: string; body: Record<string, any> }
> = ({ url, body }) => {
  return post<CommonResponse>(url, { body })
}

export const fetchMembers: Fetcher<
  { accounts: Member[] | null },
  { url: string; params: Record<string, any> }
> = ({ url, params }) => {
  return get<{ accounts: Member[] | null }>(url, { params })
}


export const inviteMember: Fetcher<
  InvitationResponse,
  { url: string; body: Record<string, any> }
> = ({ url, body }) => {
  return post<InvitationResponse>(url, { body })
}

export const updateMemberRole: Fetcher<
  CommonResponse,
  { url: string; body: Record<string, any> }
> = ({ url, body }) => {
  return put<CommonResponse>(url, { body })
}

export const deleteMemberOrCancelInvitation: Fetcher<
  CommonResponse,
  { url: string }
> = ({ url }) => {
  return del<CommonResponse>(url)
}

export const fetchFilePreview: Fetcher<
  { content: string },
  { fileID: string }
> = ({ fileID }) => {
  return get<{ content: string }>(`/files/${fileID}/preview`)
}

export const fetchCurrentWorkspace: Fetcher<
  ICurrentWorkspace,
  { url: string; params: Record<string, any> }
> = ({ url, params }) => {
  return get<ICurrentWorkspace>(url, { params })
}

export const updateCurrentWorkspace: Fetcher<
  ICurrentWorkspace,
  { url: string; body: Record<string, any> }
> = ({ url, body }) => {
  return post<ICurrentWorkspace>(url, { body })
}

export const fetchWorkspaces: Fetcher<
  { workspaces: IWorkspace[] },
  { url: string; params: Record<string, any> }
> = ({ url, params }) => {
  return get<{ workspaces: IWorkspace[] }>(url, { params })
}

export const switchWorkspace: Fetcher<
  CommonResponse & { new_tenant: IWorkspace },
  { url: string; body: Record<string, any> }
> = ({ url, body }) => {
  return post<CommonResponse & { new_tenant: IWorkspace }>(url, { body })
}


export const invitationCheck: Fetcher<
  CommonResponse & { is_valid: boolean; workspace_name: string },
  {
    url: string
    params: { workspace_id: string; email: string; token: string }
  }
> = ({ url, params }) => {
  return get<CommonResponse & { is_valid: boolean; workspace_name: string }>(
    url,
    { params },
  )
}

export const activateMember: Fetcher<
  CommonResponse,
  { url: string; body: any }
> = ({ url, body }) => {
  return post<CommonResponse>(url, { body })
}


export const fetchFileUploadConfig: Fetcher<
  FileUploadConfigResponse,
  { url: string }
> = ({ url }) => {
  return get<FileUploadConfigResponse>(url)
}

export const getSystemFeatures = () => {
  return get<SystemFeatures>('/system-features')
}
